/**
 * SE40 — native session transcript (Claude-Code-compatible on-disk shape, theokit-native names).
 *
 * The theokit session format IS the Claude Code record shape (a `uuid`/`parentUuid` DAG of records with
 * structured `text`/`tool_use`/`tool_result`/`thinking` blocks) — so the ecosystem's read-side tools
 * parse our sessions AND, pointed at `~/.claude`, the Claude Code CLI can `--continue` them. This module
 * is the PURE core: append-per-turn record builders + the DAG reader that rebuilds `LlmMessage[]` for
 * resume. File I/O + loop wiring + append-only compaction live in the FS store.
 *
 * Reader mirrors `claude-code-log` dag.py: leaf = a uuid never used as a parentUuid; walk parentUuid to a
 * root (cycle-break); dedup a uuid across sessions keeping the earliest; a `compact_boundary` root
 * terminates the walk so a resume-after-compaction replays only the post-boundary continuation.
 *
 * referencia: knowledge-base/references/claude-code-log/claude_code_log/dag.py + models.py
 */
import { randomUUID } from "node:crypto";
import { mkdir, readFile } from "node:fs/promises";
import { homedir } from "node:os";
import { dirname, join } from "node:path";

import type { LlmContentPart, LlmMessage, LlmToolResultPart } from "../llm/types.js";
import { redactSecrets } from "../security/redact.js";
import { replaceFileAtomic } from "./atomic-write.js";

/** One transcript record (one JSONL line). `message` absent on `system` (compact_boundary) records. */
export interface SessionRecord {
  type: "user" | "assistant" | "system";
  uuid: string;
  parentUuid: string | null;
  sessionId: string;
  timestamp: string;
  isSidechain?: boolean;
  userType?: string;
  cwd?: string;
  version?: string;
  subtype?: string;
  compactMetadata?: { preTokens: number; trigger: string };
  message?: Record<string, unknown>;
}

export interface SessionTranscriptOptions {
  cwd: string;
  sessionId: string;
  model: string;
  version?: string;
}

export interface AssistantTurn {
  text?: string;
  thinking?: string;
  toolCalls?: Array<{ id: string; name: string; input: Record<string, unknown> }>;
}

/** Claude Code path convention: cwd with every non-alphanumeric replaced by `-`. */
export function encodeProjectDir(cwd: string): string {
  return cwd.replace(/[^a-zA-Z0-9]/g, "-");
}

/** Make a caller-supplied session id safe as a path component (no traversal). */
export function safeSessionId(id: string): string {
  return id.replace(/[^a-zA-Z0-9_-]/g, "-");
}

const red = (s: string): string => redactSecrets(s);

/** Deep-redact a structured value (tool input) preserving object shape. */
function redactValue(value: unknown): unknown {
  try {
    return JSON.parse(redactSecrets(JSON.stringify(value ?? null)));
  } catch {
    return redactSecrets(String(value));
  }
}

type Block = Record<string, unknown>;

/**
 * Builds a session transcript append-by-append, maintaining the `parentUuid` chain. Each turn mints a
 * fresh `uuid` and parents on the previous record; `appendCompactBoundary` starts a NEW ROOT.
 */
export class SessionTranscript {
  #records: SessionRecord[] = [];
  #last: string | null = null;
  #opts: SessionTranscriptOptions;

  constructor(opts: SessionTranscriptOptions) {
    this.#opts = opts;
  }

  /**
   * Seed a transcript from records already on disk so a subsequent append parents
   * on the existing DAG leaf (the last record in file order). Prior records are
   * re-emitted verbatim by {@link records}, so the whole file rewrites atomically
   * as one append-only line set — never shrinking (SE40 compaction is append-only).
   */
  static fromRecords(
    prior: readonly SessionRecord[],
    opts: SessionTranscriptOptions,
  ): SessionTranscript {
    const t = new SessionTranscript(opts);
    t.#records = [...prior];
    t.#last = prior.length > 0 ? (prior[prior.length - 1]?.uuid ?? null) : null;
    return t;
  }

  #push(
    rec: Omit<SessionRecord, "uuid" | "parentUuid" | "sessionId" | "timestamp">,
  ): SessionRecord {
    const full: SessionRecord = {
      ...rec,
      uuid: randomUUID(),
      parentUuid: this.#last,
      sessionId: this.#opts.sessionId,
      timestamp: new Date().toISOString(),
      isSidechain: false,
      userType: "external",
      cwd: this.#opts.cwd,
      version: this.#opts.version ?? "1.0.0-theokit",
    };
    this.#records.push(full);
    this.#last = full.uuid;
    return full;
  }

  appendUserTurn(text: string): SessionRecord {
    return this.#push({
      type: "user",
      message: { role: "user", content: [{ type: "text", text: red(text) }] },
    });
  }

  appendAssistantTurn(turn: AssistantTurn): SessionRecord {
    const content: Block[] = [];
    if (turn.thinking) content.push({ type: "thinking", thinking: red(turn.thinking) });
    if (turn.text) content.push({ type: "text", text: red(turn.text) });
    for (const c of turn.toolCalls ?? []) {
      content.push({ type: "tool_use", id: c.id, name: c.name, input: redactValue(c.input) });
    }
    return this.#push({
      type: "assistant",
      message: {
        id: randomUUID(),
        type: "message",
        role: "assistant",
        model: this.#opts.model,
        content,
      },
    });
  }

  appendToolResults(
    results: Array<{ toolUseId: string; content: string; isError?: boolean }>,
  ): SessionRecord {
    const content: Block[] = results.map((r) => ({
      type: "tool_result",
      tool_use_id: r.toolUseId,
      content: red(r.content),
      is_error: r.isError === true,
    }));
    return this.#push({ type: "user", message: { role: "user", content } });
  }

  /** Append-only compaction: a `compact_boundary` system record becomes a NEW ROOT (parentUuid null). */
  appendCompactBoundary(meta: { preTokens: number; trigger: string }): SessionRecord {
    const rec: SessionRecord = {
      type: "system",
      uuid: randomUUID(),
      parentUuid: null,
      sessionId: this.#opts.sessionId,
      timestamp: new Date().toISOString(),
      subtype: "compact_boundary",
      compactMetadata: meta,
      isSidechain: false,
      userType: "external",
      cwd: this.#opts.cwd,
      version: this.#opts.version ?? "1.0.0-theokit",
    };
    this.#records.push(rec);
    this.#last = rec.uuid;
    return rec;
  }

  records(): readonly SessionRecord[] {
    return this.#records;
  }
}

// ─── Reader (DAG → LlmMessage[]) ──────────────────────────────────────────────────────────────────

/** Earliest timestamp seen per session id. */
function sessionFirstTimestamps(records: readonly SessionRecord[]): Map<string, string> {
  const first = new Map<string, string>();
  for (const r of records) {
    const cur = first.get(r.sessionId);
    if (cur === undefined || r.timestamp < cur) first.set(r.sessionId, r.timestamp);
  }
  return first;
}

/** Dedup a uuid across sessions: keep the record whose session's earliest timestamp is smallest. */
function dedupByUuid(records: readonly SessionRecord[]): Map<string, SessionRecord> {
  const firstTs = sessionFirstTimestamps(records);
  const byUuid = new Map<string, SessionRecord>();
  for (const r of records) {
    const existing = byUuid.get(r.uuid);
    const wins =
      existing === undefined ||
      (firstTs.get(r.sessionId) ?? "") < (firstTs.get(existing.sessionId) ?? "");
    if (wins) byUuid.set(r.uuid, r);
  }
  return byUuid;
}

/** Map one structured block to an LlmContentPart (`undefined` for thinking — dropped on read, SE42/#122). */
function blockToPart(b: Block): LlmContentPart | undefined {
  if (b.type === "text") return { type: "text", text: String(b.text ?? "") };
  if (b.type === "tool_use") {
    return {
      type: "tool_use",
      id: String(b.id),
      name: String(b.name),
      input: (b.input as Record<string, unknown>) ?? {},
    };
  }
  if (b.type === "tool_result") {
    const tr: LlmToolResultPart = {
      type: "tool_result",
      toolUseId: String(b.tool_use_id),
      content: typeof b.content === "string" ? b.content : JSON.stringify(b.content),
      isError: b.is_error === true,
    };
    return tr;
  }
  return undefined;
}

/** Map one record to an LlmMessage; `undefined` for non-conversational (system) records. */
function recordToMessage(rec: SessionRecord): LlmMessage | undefined {
  if (rec.type !== "user" && rec.type !== "assistant") return undefined;
  const blocks = (rec.message?.content as Block[] | undefined) ?? [];
  const parts = blocks.map(blockToPart).filter((p): p is LlmContentPart => p !== undefined);
  return { role: rec.type, content: parts };
}

/** The most-recent leaf: the LAST record (in input order) whose uuid is never used as a parentUuid. */
function findLatestLeaf(
  records: readonly SessionRecord[],
  byUuid: Map<string, SessionRecord>,
  parents: Set<string>,
): SessionRecord | undefined {
  let leaf: SessionRecord | undefined;
  for (const r of records) {
    if (byUuid.get(r.uuid) === r && !parents.has(r.uuid)) leaf = r;
  }
  return leaf;
}

/** Walk parentUuid from a leaf to a root (cycle-safe), returning root→leaf order. */
function walkToRoot(leaf: SessionRecord, byUuid: Map<string, SessionRecord>): SessionRecord[] {
  const chain: SessionRecord[] = [];
  const seen = new Set<string>();
  let cur: SessionRecord | undefined = leaf;
  while (cur !== undefined && !seen.has(cur.uuid)) {
    seen.add(cur.uuid);
    chain.push(cur);
    cur = cur.parentUuid !== null ? byUuid.get(cur.parentUuid) : undefined;
  }
  return chain.reverse();
}

/**
 * Rebuild the resumable `LlmMessage[]` from transcript records: dedup → find the most-recent leaf →
 * walk `parentUuid` to a root (a `compact_boundary` root terminates the walk) → map to LlmMessages.
 * Returns them in root→leaf (chronological replay) order.
 */
export function reconstructMessages(records: readonly SessionRecord[]): LlmMessage[] {
  const byUuid = dedupByUuid(records);
  const parents = new Set<string>();
  for (const r of byUuid.values()) if (r.parentUuid) parents.add(r.parentUuid);
  const leaf = findLatestLeaf(records, byUuid, parents);
  if (leaf === undefined) return [];

  const msgs: LlmMessage[] = [];
  for (const rec of walkToRoot(leaf, byUuid)) {
    const m = recordToMessage(rec);
    if (m !== undefined) msgs.push(m);
  }
  return msgs;
}

// ─── File I/O (the on-disk Claude layout) ─────────────────────────────────────────────────────────

/** Default transcript base dir. `~/.theokit` isolates our sessions; set to `~/.claude` for CLI interop. */
export function defaultBaseDir(): string {
  return join(homedir(), ".theokit");
}

/**
 * Expand a leading `~` / `~/` to the user's home dir. `node:path` does NOT expand `~`, so a
 * `baseDir: "~/.claude"` (the documented Claude Code CLI interop path) would otherwise resolve to a
 * literal `./~/.claude` under cwd and the CLI would never find the session. Absolute or relative
 * paths without a leading tilde pass through untouched.
 */
export function expandTilde(p: string): string {
  if (p === "~") return homedir();
  if (p.startsWith("~/")) return join(homedir(), p.slice(2));
  return p;
}

/** The `.jsonl` path for a session: `<baseDir>/projects/<encoded-cwd>/<safe-sessionId>.jsonl`. */
export function transcriptPath(baseDir: string, cwd: string, sessionId: string): string {
  return join(baseDir, "projects", encodeProjectDir(cwd), `${safeSessionId(sessionId)}.jsonl`);
}

/** Write records as one JSONL line each (atomic; parent dir created). Returns the path. */
export async function writeTranscript(
  path: string,
  records: readonly SessionRecord[],
): Promise<string> {
  await mkdir(dirname(path), { recursive: true });
  const body = records.map((r) => JSON.stringify(r)).join("\n");
  await replaceFileAtomic(path, records.length > 0 ? `${body}\n` : "");
  return path;
}

/** Parse one JSONL line to a record; `undefined` for blank/torn/invalid lines (skipped on read). */
function parseRecordLine(line: string): SessionRecord | undefined {
  const trimmed = line.trim();
  if (trimmed.length === 0) return undefined;
  try {
    const rec = JSON.parse(trimmed) as SessionRecord;
    return typeof rec.uuid === "string" && typeof rec.type === "string" ? rec : undefined;
  } catch {
    return undefined; // a torn last line (crash mid-write) — the rest of the DAG still reconstructs
  }
}

/** Parse a transcript `.jsonl` into records; a missing file yields `[]`; malformed lines are skipped. */
export async function readTranscript(path: string): Promise<SessionRecord[]> {
  let raw: string;
  try {
    raw = await readFile(path, "utf8");
  } catch (cause) {
    if ((cause as NodeJS.ErrnoException).code === "ENOENT") return [];
    throw cause;
  }
  return raw
    .split("\n")
    .map(parseRecordLine)
    .filter((r): r is SessionRecord => r !== undefined);
}
